import { Form, Input, theme, message, Popconfirm } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash';
import { useEffect, useState } from 'react';

import { useStores } from '@/hooks/use-stores';
import { useResponsive } from '@/hooks/useResponsive';
import { EPButton, EPSelect, EPSelectOption } from '@/components';
import { defaultDeviceModels } from '@/comp/MapBuilder/components/PIDEditing/DeviceDetailsCard/DynamicFormCard.utils';

import {
  useInitialValues,
  getOnDeviceSensorOptions,
  getConfirmButtonHandler,
  getConfirmButtonText,
  getCancelButtonHandler,
  getCancelButtonText,
} from './SensorForm.utils';
import { Entity } from '../../../NodeWidget.types';
import {
  getDeprecatedModelToolTip,
  getFieldSelectProps,
  IsDeprecatedModel,
  IsDisconnectedDevice,
  useDeviceModels,
} from '../../EditAssetDrawer.utils';
import { DeviceTypeSelectGlobalStyle, SensorFormContainer } from './SensorForm.styles';

interface SensorFormProps {
  parentDevice: Entity;
  onDevice: Entity;
  isAddingSensor?: boolean;
  setIsAddingSensor?: (isAddingSensor: boolean) => void;
  setHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
  afterOpenChange: boolean;
}

const SensorForm = observer(
  ({
    parentDevice,
    onDevice,
    isAddingSensor,
    setIsAddingSensor,
    setHasUnsavedChanges,
    afterOpenChange,
  }: SensorFormProps) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const initialValues = useInitialValues(onDevice);
    const { isMobile } = useResponsive();
    const { pidEditStore, stationStore } = useStores();
    const { token } = theme.useToken();
    const [isEditing, setIsEditing] = useState(isAddingSensor ? onDevice === null : false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentDeviceType, setCurrentDeviceType] = useState(onDevice?.deviceType);

    const { deviceModelOptions, isLoadingModels, onModelSearch } = useDeviceModels(
      onDevice || ({ deviceType: currentDeviceType, deviceModelId: initialValues.deviceModelId } as Entity),
      afterOpenChange,
      t,
      currentDeviceType,
      initialValues.deviceModelId as string,
    );

    const currentModelId = Form.useWatch('deviceModelId', form);
    const disconnectedDevice = IsDisconnectedDevice(deviceModelOptions, currentModelId);
    const deprecatedModel = IsDeprecatedModel(onDevice?.deviceKind);

    // Auto-select controller for initial model when deviceModelOptions are loaded
    useEffect(() => {
      if (deviceModelOptions.length > 0 && !isLoadingModels) {
        const currentModelId = form.getFieldValue('deviceModelId');
        if (currentModelId) {
          const currentModel = deviceModelOptions.find(option => String(option.value) === String(currentModelId));
          if (currentModel?.controllerId) {
            form.setFieldValue('controllerId', String(currentModel.controllerId));
          } else {
            // Clear controller if model has no controller
            form.setFieldValue('controllerId', undefined);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceModelOptions, isLoadingModels]);

    useEffect(() => {
      if (isAddingSensor && onDevice !== null) setIsEditing(false);
    }, [isAddingSensor]);

    const onCancelClick = () => {
      setHasUnsavedChanges(false);
      form.resetFields();
      setIsEditing(false);
      if (isAddingSensor) setIsAddingSensor(false);
    };

    const onCancelDelete = () => setIsDeleting(false);

    const onEditClick = () => setIsEditing(true);

    const onSaveClick = async () => {
      try {
        await pidEditStore.updateDevice(onDevice.deviceId, omit(form.getFieldsValue(), 'deviceType'));
        await pidEditStore.getEditDraft(stationStore.currentStation?.id);
        setIsEditing(false);
      } catch (error) {
        message.error(error?.message);
      }
    };

    const onTypeChange = value => {
      setCurrentDeviceType(value);
      form.setFieldValue('deviceType', value);
      form.setFieldValue('deviceModelId', defaultDeviceModels[value]);
      setHasUnsavedChanges(true);
    };

    const onChange = (field: string) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(field, typeof value === 'string' ? value : value.target.value);
      setHasUnsavedChanges(true);
    };

    const onModelChange = (value: string) => {
      form.setFieldValue('deviceModelId', value);
      setHasUnsavedChanges(true);

      // Auto-select the fitting controller if the model has one (same as EditCompressorForm)
      const selectedModel = deviceModelOptions.find(option => String(option.value) === String(value));
      if (selectedModel?.controllerId) {
        form.setFieldValue('controllerId', String(selectedModel.controllerId));
      } else {
        form.setFieldValue('controllerId', undefined);
      }
    };

    const onAddClick = async () => {
      try {
        await pidEditStore.addOnDevice(parentDevice, form.getFieldsValue());
        await pidEditStore.getEditDraft(stationStore.currentStation?.id);
        setIsAddingSensor(false);
        setHasUnsavedChanges(false);
      } catch (error) {
        message.error(error?.message);
        message.error(`Failed to Add On device: ${error.message || JSON.stringify(error)}`);
      }
    };

    const onDeleteClick = async () => {
      setIsDeleting(true);
    };

    const handleOnDelete = async () => {
      try {
        const toDevices = pidEditStore.getDraftDeviceWithoutPipesByIdToDevices(parentDevice.deviceId);

        await pidEditStore.updateDevice(parentDevice.deviceId, {
          physicalSchema: {
            toDevices,
            relatedDevices: parentDevice.relatedDevices,
            onDevices: parentDevice.onDevices.filter(device => device !== onDevice.deviceId),
            elementIndex: parentDevice.elementIndex,
            primaryFlowSensor: parentDevice.primaryFlowSensor,
            primaryPressureSensor: parentDevice.primaryPressureSensor,
            isIndependent: parentDevice.isIndependent,
          },
          controllerId: parentDevice.controllerId,
        });

        await pidEditStore.deleteDevice(onDevice);
      } catch (error) {
        message.error(`Failed to delete On device: ${error.message || JSON.stringify(error)}`, token, 8);
      } finally {
        setIsDeleting(false);
      }
    };

    return (
      <SensorFormContainer>
        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          clearOnDestroy
          className="sensor-form"
          disabled={pidEditStore.isLoadingDraft || pidEditStore.isLoadingModelSpec}
        >
          <DeviceTypeSelectGlobalStyle />
          <Form.Item label={t('name')} name="name">
            <Input size="large" disabled={!isEditing || pidEditStore.isLoadingModelSpec} onChange={onChange('name')} />
          </Form.Item>
          <Form.Item label={t('type')} name="deviceType">
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectType'), 'deviceType')}
              disabled={onDevice !== null}
              options={getOnDeviceSensorOptions(t, token)}
              popupClassName="device-type-select"
              className="device-type-select"
              onChange={onTypeChange}
            />
          </Form.Item>
          <Form.Item
            label={t('modelSpec')}
            name="deviceModelId"
            tooltip={deprecatedModel ? getDeprecatedModelToolTip() : undefined}
          >
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectModelSpec'), 'deviceModelId')}
              loading={isLoadingModels}
              disabled={!isEditing || isLoadingModels || deprecatedModel}
              onChange={onModelChange}
              onSearch={onModelSearch}
              filterOption={false}
            >
              {deviceModelOptions.map(option => (
                <EPSelectOption key={option.value} value={option.value}>
                  {option.label}
                </EPSelectOption>
              ))}
            </EPSelect>
          </Form.Item>
          <Form.Item label={t('controller')} name="controllerId">
            <EPSelect
              {...getFieldSelectProps(isMobile, form, t('selectController'), 'controllerId')}
              options={pidEditStore.deviceControllers}
              disabled={!isEditing || disconnectedDevice}
              onChange={onChange('controllerId')}
            />
          </Form.Item>
          <div className="sensor-form-footer">
            <Popconfirm
              placement="top"
              open={isDeleting}
              title={t('Remove this asset?')}
              description={t('Deleting the device will delete its historical data, do you want to proceed?')}
              onConfirm={handleOnDelete}
              onCancel={onCancelDelete}
              okText={t('Remove Asset')}
              cancelText={t('Cancel')}
              icon={null}
            >
              <EPButton type="link" onClick={getCancelButtonHandler(isEditing, onCancelClick, onDeleteClick)}>
                {t(getCancelButtonText(isEditing, onCancelClick, onDeleteClick))}
              </EPButton>
            </Popconfirm>
            <EPButton
              type="primary"
              onClick={getConfirmButtonHandler(isAddingSensor, isEditing, onAddClick, onSaveClick, onEditClick)}
            >
              {t(getConfirmButtonText(isAddingSensor, isEditing, onAddClick, onSaveClick, onEditClick))}
            </EPButton>
          </div>
        </Form>
      </SensorFormContainer>
    );
  },
);

export default SensorForm;
